import React, { useState } from 'react';

import Button from 'components/button/Button';
import InputContainer from 'components/input/InputContainer';
import ContentModalHeader from 'components/modal/ContentModalHeader';
import { usePatchPassword } from 'features/auth/api/query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { isValidPassword, removeWhitespace } from 'shared/lib';

interface ResetStep1Props {
  onClickNext: () => void;
}

const ResetStep1 = ({ onClickNext }: ResetStep1Props) => {
  const { t } = useTranslation('auth');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const { mutateAsync: tryChangePassword, isPending } = usePatchPassword({
    onSuccess: () => {
      onClickNext();
    },
    onError: (error) => {
      alert(error);
    },
  });

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = removeWhitespace(e.target.value);
    setPasswordError('');
    setPassword(value);
  };

  const onChangeConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = removeWhitespace(e.target.value);
    setConfirmPasswordError('');
    setConfirmPassword(value);

    if (value !== '' && password !== value) {
      setConfirmPasswordError(t('resetPassword.errors.passwordMismatch'));
    }
  };

  const handleNext = async () => {
    if (isPending) return;

    if (token === '') {
      alert(t('resetPassword.errors.tokenRequired'));
      return;
    }

    if (password === '' || !isValidPassword(password)) {
      setPasswordError(t('resetPassword.errors.invalidPasswordFormat'));
      return;
    }

    if (confirmPassword === '' || confirmPassword !== password) {
      setConfirmPasswordError(t('resetPassword.errors.passwordMismatch'));
      return;
    }

    try {
      await tryChangePassword({ password, passwordConfirm: confirmPassword, resetPasswordToken: token });
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <div className="flex w-[309px] max-w-full flex-col gap-y-5 px-5 py-6 md:mx-45 md:my-30 md:gap-y-6.25 md:p-0">
      <div className="flex w-full flex-col gap-y-6">
        <div className="flex w-full flex-col gap-y-1">
          <ContentModalHeader src="/images/optimized/illusts/il_lock.webp" title="Reset Password" />
          <div className="w-full whitespace-pre-wrap break-words text-center text-c2 text-gray-40">
            Create a new password.{' '}
            <p className="text-primary">
              The password must be at least 8 characters long and include lowercase letters, uppercase letters, and
              numbers.
            </p>
          </div>
        </div>
        <div className="flex w-full flex-col gap-y-1.25">
          <InputContainer
            label="New password"
            placeholder="Enter your password"
            type="password"
            error={passwordError}
            value={password}
            onChange={onChangePassword}
          />
          <InputContainer
            label="Confirm password"
            placeholder="Confirm your password"
            type="password"
            error={confirmPasswordError}
            value={confirmPassword}
            onChange={onChangeConfirmPassword}
          />
        </div>
      </div>
      <Button text="Update password" shape="SQUARE" onClick={handleNext} disabled={isPending} />
    </div>
  );
};

export default ResetStep1;
